import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EventProvider } from './contexts/EventContext';
import { OrganizerAuthProvider, useOrganizerAuth } from './contexts/OrganizerAuthContext';

// Pages
import HomePage from './pages/HomePage';
import EventLandingPage from './pages/EventLandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyCodePage from './pages/VerifyCodePage';
import TermsPage from './pages/TermsPage';
import OrganizerLoginPage from './pages/OrganizerLoginPage';
import CheckinScannerPage from './pages/CheckinScannerPage';
import NetworkingParticipantsPage from './pages/NetworkingParticipantsPage';
import NetworkingProfilePage from './pages/NetworkingProfilePage';

// Dashboards
import Dashboard from './components/Dashboard';
import OrganizerDashboard from './components/OrganizerDashboard';

// Protection des routes participant
const ProtectedRoute = ({ children, slug }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (!user) return <Navigate to={slug ? `/event/${slug}/login` : '/login'} replace />;
  return children;
};

// Protection des routes participant avec slug (lit le slug depuis l'URL)
const ProtectedRouteWithSlug = ({ children }) => {
  const { slug } = useParams();
  return <ProtectedRoute slug={slug}>{children}</ProtectedRoute>;
};

// Protection des routes organisateur
const OrganizerProtectedRoute = ({ children, slug }) => {
  const { organizer } = useOrganizerAuth();
  if (!organizer) return <Navigate to={slug ? `/event/${slug}/admin/login` : '/admin/login'} replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <EventProvider>
        <OrganizerAuthProvider>
        <Router>
          <Routes>
            {/* Page d'accueil */}
            <Route path="/" element={<HomePage />} />

            {/* Landing page événement */}
            <Route path="/event/:slug" element={<EventLandingPage />} />

            {/* Authentification — routes avec et sans slug */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/event/:slug/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/event/:slug/register" element={<RegisterPage />} />
            <Route path="/verify" element={<VerifyCodePage />} />
            <Route path="/event/:slug/verify" element={<VerifyCodePage />} />

            {/* Dashboard (protégé) — avec et sans slug */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:slug/dashboard"
              element={
                <ProtectedRouteWithSlug>
                  <Dashboard />
                </ProtectedRouteWithSlug>
              }
            />

            {/* Networking participants — public, sans auth */}
            <Route path="/event/:slug/participants-networking" element={<NetworkingParticipantsPage />} />
            <Route path="/event/:slug/participants-networking/:recordId" element={<NetworkingProfilePage />} />

            {/* Terms & Conditions */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/event/:slug/terms" element={<TermsPage />} />

            {/* Check-in Scanner */}
            <Route path="/event/:slug/checkin" element={<CheckinScannerPage />} />
            <Route path="/checkin" element={<CheckinScannerPage />} />

            {/* Organizer — login et dashboard */}
            <Route path="/event/:slug/admin/login" element={<OrganizerLoginPage />} />
            <Route path="/admin/login" element={<OrganizerLoginPage />} />
            <Route path="/event/:slug/admin" element={
              <OrganizerProtectedRoute slug=":slug">
                <OrganizerDashboard />
              </OrganizerProtectedRoute>
            } />
            <Route path="/admin" element={
              <OrganizerProtectedRoute>
                <OrganizerDashboard />
              </OrganizerProtectedRoute>
            } />

            {/* Redirection par défaut */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        </OrganizerAuthProvider>
      </EventProvider>
    </AuthProvider>
  );
}

export default App;